import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import moment from 'moment';
import Row from '../../../core/layout/Row';
import Column from '../../../core/layout/Column';
import TextInput from '../../../core/form/TextInput';
import FormButtons from '../../../core/form/FormButtons';
import FetchSelect from '../../../core/form/FetchSelect';
import Select from '../../../core/form/Select';
import DateInput from '../../../core/form/DateInput';
import Switch from '../../../core/form/Switch';
import LocaleSelect from '../../../core/form/LocaleSelect';
import { Roles } from '../../../core/util';
import PermissionValidator from '../../../core/layout/PermissionValidator';

const StudentForm = (props) => {
  const isDisabled =
    props.submitting ||
    ([Roles.COMPANY_MANAGER, Roles.SCHOOL_MANAGER, Roles.TEACHER].some((role) => role === localStorage.role) &&
      get(props.values, 'demoStudent', false));

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      <Row>
        <Column size={3}>
          <TextInput
            required
            disabled={isDisabled}
            label="Student Name"
            value={get(props.values, 'name', '')}
            onChange={(value) => props.onChange('name', value)}
            errorText={get(props.errors, 'name', null)}
          />
        </Column>
        <Column size={2}>
          <TextInput
            disabled={isDisabled}
            label="ID"
            value={get(props.values, 'cpf', '')}
            onChange={(value) => props.onChange('cpf', value)}
            errorText={get(props.errors, 'cpf', '')}
          />
        </Column>
        <Column size={4}>
          <TextInput
            required
            disabled={isDisabled}
            label="Email"
            value={get(props.values, 'email', '')}
            onChange={(value) => props.onChange('email', (value || '').toLowerCase())}
            errorText={get(props.errors, 'email', null)}
          />
        </Column>
        <Column size={2}>
          <TextInput
            required={!props.values.id}
            type="password"
            disabled={props.submitting}
            label="Password"
            value={get(props.values, 'password', '')}
            onChange={(value) => props.onChange('password', value)}
            errorText={get(props.errors, 'password', null)}
          />
        </Column>
        <Column size={1}>
          <LocaleSelect
            required
            onChange={(value) => props.onChange('locale', value)}
            disabled={isDisabled}
            value={get(props.values, 'locale', '')}
            errorText={get(props.errors, 'locale', null)}
          />
        </Column>
      </Row>
      <Row>
        <Column size={1.5}>
          <TextInput
            disabled={isDisabled}
            label="Phone"
            value={get(props.values, 'contactPhone', '')}
            onChange={(value) => props.onChange('contactPhone', value)}
            errorText={get(props.errors, 'contactPhone', null)}
          />
        </Column>
        <Column size={1.5}>
          <Select
            label="Student Demo"
            disabled={
              isDisabled ||
              ![Roles.ADMIN, Roles.SUPPORT, Roles.DISTRIBUTOR_MANAGER].some((role) => role === localStorage.role)
            }
            value={get(props.values, 'demoStudent', false) ? 'yes' : 'no'}
            onChange={(value) => {
              props.onChange('demoStudent', value === 'yes');
              if (value === 'yes' && !get(props.values, 'accessStartsAt', false)) {
                props.onChange('accessStartsAt', moment());
              }
              if (value === 'yes' && !get(props.values, 'accessEndsAt', false)) {
                props.onChange('accessEndsAt', moment().add(10, 'days'));
              }
              props.onChange('allowAllCourseContent', false);
            }}
            errorText={get(props.errors, 'demoStudent', null)}
            options={[
              { label: 'Yes, it is a demo', value: 'yes' },
              { label: 'No, it is not a demo', value: 'no' },
            ]}
          />
        </Column>
        <Column size={1.5}>
          <DateInput
            disabled={props.submitting}
            required={props.values.demoStudent}
            label="Access Period Start"
            value={get(props.values, 'accessStartsAt', null)}
            onChange={(value) => props.onChange('accessStartsAt', value)}
            errorText={get(props.errors, 'accessStartsAt', '')}
          />
        </Column>
        <Column size={1.5}>
          <DateInput
            disabled={props.submitting}
            required={props.values.demoStudent}
            label="Access Period End"
            value={get(props.values, 'accessEndsAt', null)}
            onChange={(value) => props.onChange('accessEndsAt', value)}
            errorText={get(props.errors, 'accessEndsAt', '')}
          />
        </Column>
      </Row>
      <Row>
        {!props.values.id && (
          <Column size={1.5}>
            <FetchSelect
              url="academic-plans"
              fullWidth
              disabled={isDisabled}
              label="Academic Plan"
              value={get(props.values, 'academicPlan', '')}
              onChange={(value) => {
                props.onChange('academicPlan', value);
                props.onChange('currentCourse', null);
              }}
              errorText={get(props.errors, 'academicPlan', '')}
              resultTransformer={{
                text: 'name',
                value: 'id',
              }}
            />
          </Column>
        )}
        {!props.values.id && (
          <Column size={1.5}>
            <FetchSelect
              url={`academic-plans/${get(props.values, 'academicPlan', null)}/courses`}
              fullWidth
              disabled={isDisabled || !get(props.values, 'academicPlan', null)}
              label="Current course"
              value={get(props.values, 'currentCourse', '')}
              onChange={(value) => props.onChange('currentCourse', value)}
              errorText={get(props.errors, 'currentCourse', '')}
              resultTransformer={{
                text: 'name',
                value: 'id',
              }}
            />
          </Column>
        )}
        {props.values.currentCourse && props.values.demoStudent && !props.values.id && (
          <Column size={1.5}>
            <Select
              label="Allow all course content?"
              disabled={isDisabled}
              value={get(props.values, 'allowAllCourseContent', false) ? 'yes' : 'no'}
              onChange={(value) => {
                props.onChange('allowAllCourseContent', value === 'yes');
              }}
              errorText={get(props.errors, 'allowAllCourseContent', null)}
              options={[
                { label: 'Yes, allow everything', value: 'yes' },
                { label: 'No, only the first group', value: 'no' },
              ]}
            />
          </Column>
        )}
        {!props.values.id && (
          <Column size={1}>
            <Switch
              label="Welcome Email"
              titleOff="Don't send"
              titleOn="Send"
              onChange={(value) => props.onChange('sendWelcomeEmail', value)}
              value={get(props.values, 'sendWelcomeEmail', false)}
              disabled={isDisabled}
            />
          </Column>
        )}
        {props.values.id && (
          <PermissionValidator
            allowedFor={[Roles.ADMIN, Roles.SUPPORT, Roles.DISTRIBUTOR_MANAGER, Roles.COMPANY_MANAGER]}
          >
            <Column size={2.5}>
              <FetchSelect
                showSearch
                isPaginated
                url="schools"
                fullWidth
                required
                disabled={isDisabled}
                label="School"
                value={get(props.values, 'schoolClass.school.id', '')}
                onChange={(schoolId) => {
                  props.onChange('schoolClass.school.id', schoolId);
                  props.onChange('schoolClass.id', undefined);
                }}
                description={get(props.errors, 'schoolClass.school.id', null)}
                fieldValidation={get(props.errors, 'schoolClass.school.id', null) && 'error'}
                resultTransformer={{
                  text: 'name',
                  value: 'id',
                }}
              />
            </Column>
          </PermissionValidator>
        )}
        {props.values.id && (
          <Column
            size={[Roles.ADMIN, Roles.SUPPORT, Roles.DISTRIBUTOR_MANAGER].some((r) => r === localStorage.role) ? 2 : 4}
          >
            <FetchSelect
              showSearch
              isPaginated
              params={{
                school: get(props.values, 'schoolClass.school.id', ''),
              }}
              url={`schools/${props.values.schoolClass.school.id}/classes`}
              fullWidth
              disabled={isDisabled}
              label="School Class"
              value={get(props.values, 'schoolClass.id', '')}
              onChange={(schoolClassId) => {
                props.onChange('schoolClass.id', schoolClassId);
              }}
              description={get(props.errors, 'schoolClass', null)}
              fieldValidation={get(props.errors, 'schoolClass', null) && 'error'}
              resultTransformer={{
                text: 'name',
                value: 'id',
              }}
            />
          </Column>
        )}
      </Row>
      <FormButtons
        confirmLabel={props.values.id ? 'Update Student' : 'Create Student'}
        isDisabled={props.submitting || !props.isDirty()}
        isSubmitting={props.submitting}
        onReset={props.onReset}
      />
    </form>
  );
};

StudentForm.propTypes = {
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  isDirty: PropTypes.func,
};

StudentForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  isDirty: () => false,
  onSubmit: () => alert('submitted'),
  onReset: () => false,
};

export default StudentForm;
